import type { TracesSamplerSamplingContext } from "@sentry/core";
import * as Sentry from "@sentry/react";
import _ from "lodash";
import { getReleaseName } from "../../legacy/utils/get-release-name";
import type { EnvName } from "../../legacy/utils/getEnvName";
import { getEnvName } from "../../legacy/utils/getEnvName";
import { isChunkLoadError } from "../chunk-load-recovery/is-chunk-load-error";
import { getHttpClientErrorFingerprint } from "./http-client-error-fingerprint";

const STATIC_FILE_PATTERN = /\.(css|js|png|jpg|svg|woff|ttf)$/i;

const ANALYTICS_HOSTS_PATTERN =
  /youwillneverfindme\.bonusx\.it|mixpanel\.com|analytics\.google\.com|google-analytics\.com|googletagmanager\.com|appsflyersdk\.com|clarity\.ms/i;

export function isAnalyticsUrl(url: string | undefined): boolean {
  if (!url) return false;
  if (ANALYTICS_HOSTS_PATTERN.test(url)) return true;
  return url.includes("/analytics") || url.includes("/tracking") || url.includes("/pixel");
}

function getErrorSampleRate(envName?: EnvName): number {
  switch (envName) {
    case "local":
      return 0;
    case "development":
      return 0.1;
    case "staging":
      return 0.3;
    case "production":
      return 0.5;
    default:
      return 0.1;
  }
}

function getSampleRateLevel(envName?: EnvName) {
  switch (envName) {
    case "local":
      return 0;
    case "development":
      return 0.3;
    case "staging":
      return 0.5;
    case "production":
      return 0.1;
    default:
      return 0;
  }
}

function getProfilesSampleRate(envName?: EnvName): number {
  switch (envName) {
    case "local":
      return 0;
    case "development":
      return 0;
    case "staging":
      return 0.1;
    case "production":
      return 0.05;
    default:
      return 0;
  }
}

function getReplaySampleRateLevel(envName?: EnvName) {
  switch (envName) {
    case "local":
      return 0;
    case "development":
      return 0;
    case "staging":
      return 0.1;
    case "production":
      return 0.005;
    default:
      return 0;
  }
}

function getReplayOnErrorSampleRateLevel(envName?: EnvName) {
  switch (envName) {
    case "local":
      return 0;
    case "development":
      return 0;
    case "staging":
      return 1;
    case "production":
      return 0.3;
    default:
      return 0;
  }
}

function createTracesSampler(envName: EnvName, defaultSampleRate: number) {
  return function tracesSampler(samplingContext: TracesSamplerSamplingContext): number {
    const { name, parentSampled } = samplingContext;

    if (parentSampled !== undefined) {
      return parentSampled ? 1.0 : 0;
    }

    if (name?.match(/health|ping|heartbeat/i)) {
      return 0;
    }

    if (name?.match(STATIC_FILE_PATTERN)) {
      return 0.01;
    }

    if (name?.match(/^(GET|POST|PUT|DELETE)/i)) {
      return envName === "production" ? 0.1 : 0.5;
    }

    return defaultSampleRate;
  };
}

const ENABLED_ENV_NAMES: EnvName[] = ["production", "staging"];

const envName = getEnvName();
const sampleRateLevel = getSampleRateLevel(envName);
const errorSampleRate = getErrorSampleRate(envName);
const profilesSampleRate = getProfilesSampleRate(envName);
const replayRateLevel = getReplaySampleRateLevel(envName);
const replayOnErrorRateLevel = getReplayOnErrorSampleRateLevel(envName);

const ignoreErrors = [
  /fb_xd_fragment/gim,
  /findDOMNode/gim,
  /ResizeObserver/gim,
  /UNSAFE_componentWillMount/gim,
  /Illegal/gim,
  /Network Error/gim,
  /Request failed with status code 4\d{2}/gim,
  /Request aborted/gim,
  /timeout of \d+ms exceeded/gim,
  /canceled/gim,
  /chrome-extension:/i,
  /moz-extension:/i,
  /safari-extension:/i,
  /adblock/i,
  /blocked by client/i,
  /CORS/i,
  /AbortError/i,
  /NotAllowedError/i,
  /SecurityError/i,
  /Non-Error promise rejection captured/i,
  /TypeError: Failed to fetch/i,
  /TypeError: Load failed/i,
  /NetworkError when attempting to fetch resource/i,
  /TypeError: The network connection was lost/i,
  /^cancelled$|The operation was cancelled/i,
  /Object Not Found Matching Id/i,
  /window\.webkit\.messageHandlers/i,
  /Config JSON is not present when evaluating setting/i,
  /window\.clarity is not a function/i,
  /enableDidUserTypeOnKeyboardLogging/i,
  /status 5\d\d/i,
];

if (envName === "production") {
  ignoreErrors.push(/DevCycle/gim);
}

function isHttpClientError(exception: unknown): boolean {
  if (!exception || typeof exception !== "object") return false;

  const error = exception as Record<string, unknown>;
  const status = _.get(error, "status") ?? _.get(error, "response.status");

  const isHttpClientError = typeof status === "number" && status >= 400 && status < 500;
  return isHttpClientError;
}

const STALE_HTML_SYNTAX_ERROR_PATTERN = /Unexpected token '<'|expected expression, got '<'/i;
const HASHED_BUNDLE_FILENAME_PATTERN = /static\/js\/.*\.bundle\.js/;

// A stale index.html served in place of a missing hashed chunk gets parsed as JS and throws this SyntaxError
function isStaleHtmlSyntaxErrorEvent(event: Sentry.ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.type !== "SyntaxError") {
    return false;
  }
  if (!exception.value || !STALE_HTML_SYNTAX_ERROR_PATTERN.test(exception.value)) {
    return false;
  }

  const topFrame = exception.stacktrace?.frames?.at(-1);
  return Boolean(topFrame?.filename && HASHED_BUNDLE_FILENAME_PATTERN.test(topFrame.filename));
}

function isThirdPartyScript(filename: string): boolean {
  if (filename === "<anonymous>" || filename.startsWith("[native") || !filename.startsWith("http")) {
    return false;
  }

  const allowedOrigins = ["bonusx.it"];
  return !allowedOrigins.some(function isAllowedOrigin(origin) {
    return filename.includes(origin);
  });
}

function shouldCreateSpanForRequest(url: string): boolean {
  const isStaticFile = url.match(STATIC_FILE_PATTERN);
  return !isStaticFile && !isAnalyticsUrl(url);
}

export const sentryConfig: Sentry.BrowserOptions = {
  release: `bx-feapp-platform@${getReleaseName()}`,
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_ENV_NAME,
  enabled: ENABLED_ENV_NAMES.includes(envName),
  sampleRate: errorSampleRate,
  tracesSampler: createTracesSampler(envName, sampleRateLevel),
  profilesSampleRate,
  replaysSessionSampleRate: replayRateLevel,
  replaysOnErrorSampleRate: replayOnErrorRateLevel,
  sendDefaultPii: envName !== "local",
  normalizeDepth: 3,
  maxValueLength: 250,
  maxBreadcrumbs: 50,
  integrations: [
    Sentry.dedupeIntegration(),
    Sentry.browserTracingIntegration({
      enableInp: true,
      shouldCreateSpanForRequest,
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.captureConsoleIntegration({ levels: ["error"] }),
    Sentry.extraErrorDataIntegration(),
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [[500, 599]],
    }),
    Sentry.contextLinesIntegration(),
    Sentry.replayIntegration({
      maskAllText: envName === "production",
      maskAllInputs: true,
      blockAllMedia: true,
      block: [".sensitive-data", "[data-sensitive]"],
    }),
  ],
  ignoreErrors,
  ignoreTransactions: [/^\/health/i, /^\/ping/i, /^\/heartbeat/i, /favicon\.ico/i, /robots\.txt/i, /^\/admin/i],
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
  ],
  beforeSend(event, hint) {
    if (window.location.pathname.startsWith("/admin")) {
      return null;
    }

    if (isHttpClientError(hint?.originalException)) {
      return null;
    }

    const httpClientErrorFingerprint = getHttpClientErrorFingerprint(event);
    if (httpClientErrorFingerprint) {
      event.fingerprint = httpClientErrorFingerprint;
    }

    if (event.exception?.values?.length) {
      const frames = event.exception.values[0]?.stacktrace?.frames;
      if (frames?.length) {
        const lastFrame = frames.at(-1);
        if (lastFrame?.filename && isThirdPartyScript(lastFrame.filename)) {
          return null;
        }
      }
    }

    const origException = hint?.originalException;
    if (origException instanceof Error) {
      const msg = origException.message;

      const isNetworkConnectivityError =
        /Failed to fetch|Load failed|NetworkError when attempting to fetch|The network connection was lost|Network Error/i.test(
          msg
        );
      if (isChunkLoadError(origException)) {
        event.fingerprint = ["{{ default }}", "chunk-load-error"];
      } else if (isNetworkConnectivityError) {
        event.fingerprint = ["{{ default }}", "network-connectivity-error"];
      }
    }

    if (isStaleHtmlSyntaxErrorEvent(event)) {
      event.fingerprint = ["{{ default }}", "chunk-load-error"];
      event.tags = { ...event.tags, chunk_recovery: "stale-html-syntax" };
    }

    if (event.request?.url) {
      event.request.url = event.request.url
        .replace(/([?&]token=)[^&]*/, "$1[Filtered]")
        .replace(/([?&]email=)[^&]*/, "$1[Filtered]")
        .replace(/([?&]api_key=)[^&]*/, "$1[Filtered]");
    }

    if (event.request?.headers) {
      delete event.request.headers["Authorization"];
      delete event.request.headers["Cookie"];
    }

    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map(function mapBreadcrumb(breadcrumb) {
        if (breadcrumb.data?.url) {
          breadcrumb.data.url = breadcrumb.data.url.replace(/([?&]token=)[^&]*/, "$1[Filtered]");
        }
        return breadcrumb;
      });
    }

    return event;
  },
  beforeSendTransaction(event) {
    if (event.transaction?.match(/^\/admin/i)) {
      return null;
    }

    if (event.timestamp != null && event.start_timestamp != null) {
      const durationMs = (event.timestamp - event.start_timestamp) * 1000;
      if (durationMs < 5) {
        return null;
      }
    }

    if (event.transaction?.match(/^\/(health|ping|metrics)/i)) {
      return null;
    }

    return event;
  },
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === "console" && breadcrumb.level === "log") {
      return null;
    }

    if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
      const url = breadcrumb.data?.url;
      if (isAnalyticsUrl(url)) {
        return null;
      }
    }

    if (breadcrumb.data?.url) {
      breadcrumb.data.url = breadcrumb.data.url.replace(/([?&]token=)[^&]*/, "$1[Filtered]");
    }

    return breadcrumb;
  },
};
